import cx from 'classnames';
import React, { useEffect, useRef } from 'react';

import { ImageAlignment, AccordionAssembly, Subject } from 'api/contentful/contentTypes.cloverdotcom';
import { useBreakpoints } from 'hooks/useBreakpoints';
import debounce from 'lodash/debounce';
import type { AppType, Image as ImageType } from 'services/cms/typesUtil';
import { ImageModule } from './ImageModule';
import styles from './ImageTextModule.module.scss';
import { TextModule } from './TextModule';
import { IMAGE_TEXT_MODULE_TEST_ID } from './ImageTextModule.const';

type ImageTextModuleProps = AppType.Component<{
  header: string;
  headline: string;
  image: ImageType;
  isImageTransparent: boolean;
  imageAlignment: string;
  description?: string;
  imageTextModuleCta?: Subject[];
  accordion: AccordionAssembly;
  backgroundColor: string;
}>;

export const ImageTextModule: React.FC<ImageTextModuleProps> = ({
  header,
  headline,
  image,
  isImageTransparent,
  imageAlignment,
  description,
  imageTextModuleCta,
  accordion,
  backgroundColor,
  ...props
}) => {
  const { browser } = useBreakpoints();
  const isMobile = browser.is.mobile;
  const isImageAlignLeft = imageAlignment === ImageAlignment.LEFT;

  const imageTextSectionRef = useRef<HTMLDivElement>(null);
  const textGridItemRef = useRef<HTMLDivElement>(null);
  const textGridContainerRef = useRef<HTMLDivElement>(null);
  const imageGridContainerRef = useRef<HTMLDivElement>(null);

  const updateGridProperties = () => {
    const imageGridContainerHeight = imageGridContainerRef?.current?.clientHeight ?? 0;
    const textGridContainerHeight = textGridContainerRef?.current?.clientHeight ?? 0;
    const textGridItemHeight = textGridItemRef?.current?.clientHeight ?? 0;

    const setTextGridContainerProperty = (prop = '', val = '') => {
      textGridContainerRef?.current?.style.setProperty(prop, val);
    };

    if (imageGridContainerHeight > 0) {
      setTextGridContainerProperty('min-height', `${imageGridContainerHeight}px`);
    }

    if (isMobile && isImageTransparent) {
      setTextGridContainerProperty('min-height', `0px`);
    }

    setTextGridContainerProperty('--image-height', `${imageGridContainerHeight}px`);
    setTextGridContainerProperty('--grid-item-height', `${textGridItemHeight}px`);

    const marginTop =
      textGridItemHeight - textGridContainerHeight > 0
        ? `${textGridItemHeight - textGridContainerHeight}px`
        : '0px';
    imageTextSectionRef?.current?.style.setProperty('--grid-container-margin-top', marginTop);
  };

  useEffect(() => {
    updateGridProperties();
    const handleResize = debounce(() => {
      updateGridProperties();
    }, 100);
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const handleImageOnLoad = () => {
    updateGridProperties();
  };

  return (
    <section
      {...props._attributes}
      style={{ backgroundColor: backgroundColor || '' }}
      data-testid={IMAGE_TEXT_MODULE_TEST_ID.section}
    >
      <div
        ref={imageTextSectionRef}
        className={cx(styles.imageTextModule, styles.imageTextModuleSection, {
          [styles.imageTextModuleTransparentImg]: isImageTransparent,
        })}
      >
        <TextModule
          isImageTransparent={isImageTransparent}
          isImageAlignLeft={isImageAlignLeft}
          textGridContainerRef={textGridContainerRef}
          textGridItemRef={textGridItemRef}
          accordion={accordion}
          headline={headline}
          description={description}
          imageTextModuleCta={imageTextModuleCta}
        />
        <ImageModule
          image={image}
          isImageTransparent={isImageTransparent}
          isImageAlignLeft={isImageAlignLeft}
          imageGridContainerRef={imageGridContainerRef}
          handleImageOnLoad={handleImageOnLoad}
        />
      </div>
    </section>
  );
};
